import React, { useState, useEffect } from "react";
import {
  Heart,
  Share2,
  Coffee,
  Briefcase,
  Footprints,
  Plane,
  Trophy,
  CheckCircle2,
  X,
} from "lucide-react";

interface ProductDetailProps {
  product?: {
    id: string;
    name: string;
    category: string;
    subcategory?: string;
    description: string;
    images: Array<{ url: string; thumbnailUrl: string }>;
    prices: Record<string, { price: number; mrp: number }>;
    goodFor: Array<{ id: string; label: string }>;
    tags: string[];
  };
  selectedCurrency?: string;
}

const currencySymbols: Record<string, string> = {
  SAR: "﷼",
  AED: "د.إ",
  BHD: "BD",
  INR: "₹",
};

const goodForIcons: Record<string, React.ReactElement> = {
  Casual: <Coffee className="w-5 h-5 text-indigo-500" />,
  Office: <Briefcase className="w-5 h-5 text-sky-500" />,
  Running: <Footprints className="w-5 h-5 text-rose-500" />,
  Travel: <Plane className="w-5 h-5 text-amber-500" />,
  Sports: <Trophy className="w-5 h-5 text-emerald-500" />,
  Work: <Briefcase className="w-5 h-5 text-slate-500" />,
};

const useSwipeGesture = (onSwipeLeft: () => void, onSwipeRight: () => void) => {
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const touchEnd = e.changedTouches[0].clientX;
    const distance = touchStart - touchEnd;

    if (Math.abs(distance) > 50) {
      distance > 0 ? onSwipeLeft() : onSwipeRight();
    }
  };

  return { onTouchStart, onTouchEnd };
};

const useKeyNavigation = (onLeft: () => void, onRight: () => void) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") onLeft();
      if (e.key === "ArrowRight") onRight();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onLeft, onRight]);
};

const ImageCarousel: React.FC<{
  images: Array<{ url: string; thumbnailUrl: string }>;
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onImageClick: () => void;
}> = ({ images, currentIndex, onIndexChange, onImageClick }) => {
  const nextImage = () =>
    currentIndex < images.length - 1 && onIndexChange(currentIndex + 1);
  const prevImage = () => currentIndex > 0 && onIndexChange(currentIndex - 1);

  const swipeHandlers = useSwipeGesture(nextImage, prevImage);
  useKeyNavigation(prevImage, nextImage);

  return (
    <div className="relative">
      {/* Main Image */}
      <div
        className="relative aspect-square overflow-hidden rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 shadow-xl cursor-zoom-in"
        onClick={onImageClick}
        {...swipeHandlers}
      >
        <div
          className="flex transition-transform duration-300 ease-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {images.map((image, index) => (
            <img
              key={index}
              src={image.url}
              alt={`Product image ${index + 1}`}
              className="w-full h-full object-cover flex-shrink-0"
              draggable={false}
            />
          ))}
        </div>

        {/* Image Counter */}
        <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Navigation Arrows - Desktop */}
        <div className="hidden md:block">
          {currentIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-all duration-200 hover:scale-110"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-all duration-200 hover:scale-110"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Progress Dots - Mobile */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 md:hidden">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                onIndexChange(index);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                currentIndex === index
                  ? "bg-white shadow-lg scale-110"
                  : "bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails - Desktop */}
      <div className="mt-6 hidden md:flex gap-4 overflow-x-auto pb-2">
        {images.map((image, index) => (
          <button
            key={index}
            onClick={() => onIndexChange(index)}
            className={`relative w-20 h-20 lg:w-24 lg:h-24 rounded-xl overflow-hidden border-3 transition-all duration-300 flex-shrink-0 ${
              currentIndex === index
                ? "border-blue-500 shadow-lg scale-105"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <img
              src={image.thumbnailUrl}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover transition-transform duration-200 hover:scale-110"
            />
            {currentIndex === index && (
              <div className="absolute inset-0 bg-blue-500/20" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const FullScreenModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  images: Array<{ url: string; thumbnailUrl: string }>;
  currentIndex: number;
  onIndexChange: (index: number) => void;
}> = ({ isOpen, onClose, images, currentIndex, onIndexChange }) => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const handleEscape = (e: KeyboardEvent) =>
        e.key === "Escape" && onClose();
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.body.style.overflow = "";
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen, onClose]);

  useKeyNavigation(
    () => currentIndex > 0 && onIndexChange(currentIndex - 1),
    () => currentIndex < images.length - 1 && onIndexChange(currentIndex + 1)
  );

  if (!isOpen) return null;

  const handleImageClick = () => {
    setScale(scale === 1 ? 2 : 1);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white">
        <span className="text-lg font-medium">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Main Image */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <img
          src={images[currentIndex]?.url}
          alt={`Full screen image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain transition-transform duration-200 cursor-zoom-in"
          style={{ transform: `scale(${scale})` }}
          onClick={handleImageClick}
          draggable={false}
        />
      </div>

      {/* Thumbnails */}
      <div className="p-4 flex gap-2 overflow-x-auto bg-black/60">
        {images.map((image, index) => (
          <button
            key={index}
            onClick={() => onIndexChange(index)}
            className={`w-16 h-16 flex-shrink-0 rounded overflow-hidden border-2 transition-all ${
              currentIndex === index
                ? "border-white"
                : "border-transparent opacity-60 hover:opacity-100"
            }`}
          >
            <img
              src={image.thumbnailUrl}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const ProductDetail: React.FC<ProductDetailProps> = ({
  product,
  selectedCurrency = "SAR",
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);

  if (!product) return null;

  const pricing =
    product.prices[selectedCurrency] || Object.values(product.prices)[0];
  const discountPercentage = Math.round(
    ((pricing.mrp - pricing.price) / pricing.mrp) * 100
  );

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.description,
          url: window.location.href,
        });
      } catch {
        // Share cancelled or failed
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white min-h-screen lg:min-h-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Image Section */}
          <div className="lg:w-2/3">
            <ImageCarousel
              images={product.images}
              currentIndex={currentImageIndex}
              onIndexChange={setCurrentImageIndex}
              onImageClick={() => setIsFullScreenOpen(true)}
            />
          </div>

          {/* Product Info */}
          <div className="lg:w-1/3 lg:sticky lg:top-10 lg:self-start space-y-4">
            {/* Category & Name */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-medium">
                  {product.category}
                  {product.subcategory && ` / ${product.subcategory}`}
                </p>
              </div>

              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {product.name}
              </h1>
            </div>

            {/* Description */}
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Good for section */}
            {product.goodFor?.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-gray-800 tracking-wide">
                  Perfect For
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {product.goodFor.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-300 hover:shadow-md hover:border-blue-400 hover:-translate-y-1"
                    >
                      <div className="flex-shrink-0 bg-gray-50 p-2 rounded-lg">
                        {goodForIcons[item.label] || (
                          <CheckCircle2 className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                      <span className="font-medium text-gray-700">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-gray-800 tracking-wide">
                  Features
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, index) => (
                    <span
                      key={tag}
                      className={`text-sm px-3 py-1.5 rounded-full font-medium transition-all duration-200 hover:scale-105 ${
                        index % 3 === 0
                          ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                          : index % 3 === 1
                          ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                          : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Screen Modal */}
      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        images={product.images}
        currentIndex={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
      />
    </div>
  );
};

export default ProductDetail;
